/* eslint-disable camelcase */
// node modules
import React from 'react';
import PropTypes from 'prop-types';
import useStyles from 'isomorphic-style-loader/useStyles';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

// app modules: components
import AlacarteStatusIndicator from 'components/AlacarteStatusIndicator';
import { ContentOptionsAsIcon } from 'components/ContentOptions';
import Link from 'components/Link';
import CollectionImage from 'components/CollectionImage';
import FavouriteButton from 'components/FavouriteButton';

// app modules: services
import route from 'services/route';
import formatter from 'services/formatter';
import { getImageSrc } from 'services/image';
import { getSharingText } from 'services/socialShare';

// app modules: constants
import { CONTENT_TYPES } from 'constants/index';

// assets
import CssDescriptiveItem from './DescriptiveItem.scss';

function SearchReferenceItem(props) {
  const { item, onClick } = props;
  const { authors_, id, title, hash, is_alacarte, primary_image } = item;
  const book = {
    isbn: item.book_isbn,
    image_name: item.book_image_name,
    image_permission: true,
    title: item.book_title,
    signature_: item.book_signature_,
    authors: item.book_authors,
  };

  useStyles(CssDescriptiveItem);

  const isOwned = useSelector(state => state.user.owned_books.includes(book.isbn));
  const recipeAuthors = formatter.authorsList.toString(authors_);
  const shareImageUrl = getImageSrc(primary_image, 'square', size => size.w >= 315, {
    searchHighToLow: true,
  });
  return (
    <div
      className={classNames('descriptive-item', {
        'descriptive-item--not-owned': is_alacarte && !isOwned,
      })}
    >
      {is_alacarte ? (
        <AlacarteStatusIndicator
          isbn={item.isbn}
          signature_={item.signature_}
          className="descriptive-item__alacarte"
        />
      ) : null}
      <Link
        to={route.content(item.id, title)}
        onClick={onClick}
        className="descriptive-item__wrapper"
      >
        <header className="descriptive-item__header">
          <p className="descriptive-item__title">{title}</p>
        </header>

        <div className="descriptive-item__body ">
          <div className="descriptive-item__images">
            <CollectionImage
              className="descriptive-item__images"
              contentImage={{
                book: {
                  image_name: item.book_image_name,
                },
                primary_image: item.image?.src ? item.image : null,
              }}
              width={125}
              height={125}
              bookCoverWidth={100}
              bookCoverHeight={100}
            />
          </div>

          <div>
            <p className="descriptive-item__content">{item.description}</p>
          </div>
        </div>
        <div className="descriptive-item__footer">
          <p className="descriptive-item__footer__paragraph">
            <span className="descriptive-item__footer__paragraph--bold">{item.book_title}</span>
            <br />
            {recipeAuthors}
          </p>
        </div>
      </Link>
      {/* Interactive elements are overlayed using CSS to avoid nesting them inside the <a> tag. */}
      <div className="descriptive-item__button-wrapper">
        <div className="descriptive-item__buttons">
          <FavouriteButton contentId={id} contentType={CONTENT_TYPES.REFERENCE} hash={hash} />
          <ContentOptionsAsIcon
            authors={authors_}
            book={book}
            itemId={id}
            hash={hash}
            itemType={CONTENT_TYPES.CONTENT}
            contentType={CONTENT_TYPES.REFERENCE}
            imageToShare={shareImageUrl}
            urlToShare={route.content(id, title)}
            textToShare={getSharingText(title, book, authors_)}
          />
        </div>
      </div>
    </div>
  );
}

SearchReferenceItem.propTypes = {
  item: PropTypes.shape({
    authors: PropTypes.arrayOf(PropTypes.string),
    authors_: PropTypes.arrayOf(
      PropTypes.shape({
        avatar_name: PropTypes.string,
      }),
    ),
    book_title: PropTypes.string,
    book_signature_: PropTypes.string,
    book_image_name: PropTypes.string,
    book_isbn: PropTypes.string,
    id: PropTypes.string,
    title: PropTypes.string,
    subtitle: PropTypes.arrayOf(PropTypes.string),
    description: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func,
};

SearchReferenceItem.defaultProps = {
  onClick: () => {},
};

export default SearchReferenceItem;
